import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { AuditAuthMethod, AuditEvent, Paginated } from '@grc/types';
import { apiFetch } from '../../lib/api.js';
import { useAuth } from '../../lib/auth.js';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Input,
  LoadingPane,
  PageHeader,
  Select,
  Table,
  formatDateTime,
} from '../../components/ui.js';

const PAGE_SIZE = 50;

export function PlatformAuditPage() {
  const { me } = useAuth();
  const isSystemAdmin = me?.user.isSystemAdmin ?? false;
  const [offset, setOffset] = useState(0);
  const [filters, setFilters] = useState({
    action: '',
    entityType: '',
    orgId: '',
    authMethod: '' as AuditAuthMethod | '',
  });

  const audit = useQuery({
    queryKey: ['platform-audit', offset, filters],
    queryFn: () =>
      apiFetch<Paginated<AuditEvent>>('/admin/audit', {
        query: {
          limit: PAGE_SIZE,
          offset,
          action: filters.action || undefined,
          entityType: filters.entityType || undefined,
          orgId: filters.orgId || undefined,
          authMethod: filters.authMethod || undefined,
        },
      }),
    enabled: isSystemAdmin,
  });

  if (!isSystemAdmin) {
    return <EmptyState title="System administrators only" message="Cross-tenant audit log." />;
  }

  const setFilter = (key: keyof typeof filters) => (value: string) => {
    setFilters((f) => ({ ...f, [key]: value }));
    setOffset(0);
  };

  const items = audit.data?.items ?? [];
  const total = audit.data?.total ?? 0;

  return (
    <div>
      <PageHeader
        title="Audit log"
        subtitle="Every recorded action across every organisation, newest first"
      />

      <Card
        title={`${total.toLocaleString()} events`}
        actions={
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Input
              aria-label="Filter by action"
              placeholder="action e.g. response.updated"
              value={filters.action}
              onChange={(e) => setFilter('action')(e.target.value)}
              style={{ maxWidth: 200 }}
            />
            <Input
              aria-label="Filter by entity type"
              placeholder="entity e.g. assessment"
              value={filters.entityType}
              onChange={(e) => setFilter('entityType')(e.target.value)}
              style={{ maxWidth: 160 }}
            />
            <Input
              aria-label="Filter by organisation id"
              placeholder="org id"
              value={filters.orgId}
              onChange={(e) => setFilter('orgId')(e.target.value)}
              style={{ maxWidth: 160 }}
            />
            <Select
              aria-label="Filter by authentication method"
              value={filters.authMethod}
              onChange={(e) => setFilter('authMethod')(e.target.value as AuditAuthMethod | '')}
              style={{ maxWidth: 160 }}
            >
              <option value="">Any method</option>
              <option value="session">Browser session</option>
              <option value="api_token">API token</option>
            </Select>
          </div>
        }
      >
        {audit.isLoading ? (
          <LoadingPane label="Loading audit events" />
        ) : items.length === 0 ? (
          <p style={{ color: 'var(--color-text-muted)', margin: 0 }}>No events match these filters.</p>
        ) : (
          <>
            <Table
              head={
                <>
                  <th>When</th>
                  <th>Action</th>
                  <th>Actor</th>
                  <th>Org</th>
                  <th>Entity</th>
                </>
              }
            >
              {items.map((e) => (
                <tr key={e.id}>
                  <td style={{ whiteSpace: 'nowrap' }}>{formatDateTime(e.createdAt)}</td>
                  <td>
                    <Badge tone="neutral">{e.action}</Badge>
                  </td>
                  <td>
                    {e.userName ?? e.userId ?? 'system'}
                    {e.authMethod === 'api_token' && (
                      <div style={{ marginTop: 2 }}>
                        <Badge tone="neutral">
                          API token{e.apiTokenName ? ` · ${e.apiTokenName}` : ''}
                        </Badge>
                      </div>
                    )}
                    {e.impersonatorName && (
                      <div style={{ color: 'var(--color-warning)', fontSize: 'var(--text-xs)' }}>
                        impersonated by {e.impersonatorName}
                      </div>
                    )}
                  </td>
                  <td style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)' }}>
                    {e.orgId ? <code>{e.orgId.slice(0, 14)}…</code> : '—'}
                  </td>
                  <td style={{ fontSize: 'var(--text-xs)' }}>
                    {e.entityType}
                    {e.entityId && (
                      <span style={{ color: 'var(--color-text-faint)' }}> · {e.entityId.slice(0, 14)}…</span>
                    )}
                  </td>
                </tr>
              ))}
            </Table>
            <div style={{ display: 'flex', gap: 12, marginTop: 12, alignItems: 'center' }}>
              <Button
                size="sm"
                variant="secondary"
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              >
                Previous
              </Button>
              <span style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)' }}>
                {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total.toLocaleString()}
              </span>
              <Button
                size="sm"
                variant="secondary"
                disabled={offset + PAGE_SIZE >= total}
                onClick={() => setOffset(offset + PAGE_SIZE)}
              >
                Next
              </Button>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
